/**
 * PEOPLE — the full team directory (§3.3-11), its own page rather than a corner of Settings.
 *
 * What it does that the old team panel did not: CREATE an account with a password on the spot
 * (not only invite), RESET a password, edit name/role, enable/disable, and mark someone
 * on-call eligible with a mobile. On-call eligibility here is the same roster the On-call page
 * pages from — a person made eligible with a number shows up there to add to the ladder, and
 * disabling them takes them off it. Two views of one roster, not two lists.
 *
 * Every write is rank-gated and audited by the server; the UI mirrors the gates so a button
 * that would be refused is not offered. Passwords are typed by the operator for their own
 * team — the console never stores or reads one back.
 */
import { useMemo, useState } from "react";
import { Badge, Button, EmptyState, Field, Icon, Modal, Notice, Panel, Toggle } from "../components/ui";
import { api, ApiError } from "../../lib/api.ts";
import type { Page, Role, UserItem } from "../../lib/api.ts";
import { itemsOf, useResource, usePermissions, useSession } from "../../lib/useApi.ts";

const ROLES: Role[] = ["owner", "admin", "dispatcher", "viewer"];
const roleLabel = (r: Role): string => r.charAt(0).toUpperCase() + r.slice(1);
const RANK: Record<Role, number> = { owner: 3, admin: 2, dispatcher: 1, viewer: 0 };

type Status = { label: string; tone: "green" | "amber" | "neutral" };
function statusOf(u: UserItem): Status {
  if (u.disabledAt && !u.lastLoginAt) return { label: "Invited", tone: "amber" };
  if (u.disabledAt) return { label: "Disabled", tone: "neutral" };
  return { label: "Active", tone: "green" };
}

function lastSeen(iso: string | null): string {
  if (!iso) return "Never signed in";
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  return d.toLocaleDateString();
}

function strongPassword(): string {
  const alphabet = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const bytes = crypto.getRandomValues(new Uint32Array(16));
  const body = [...bytes].map((n) => alphabet[n % alphabet.length]).join("");
  return `${body.slice(0, 5)}-${body.slice(5, 10)}-${body.slice(10, 15)}`;
}

export function People() {
  const query = useResource<Page<UserItem>>(["users"], () => api.users(), { staleTimeMs: 20_000 });

  if (query.isLoading) {
    return (
      <div className="page-status" role="status" aria-live="polite">
        <Icon name="loader" className="spin" size={26} />
        <p>Loading people…</p>
      </div>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Panel title="People unavailable">
        <p className="muted">{(query.error as ApiError | null)?.detail || "The console could not reach NightDesk."}</p>
        <Button variant="primary" loading={query.isFetching} onClick={() => void query.refetch()}>
          <Icon name="refresh" /> Try again
        </Button>
      </Panel>
    );
  }

  return <Directory users={itemsOf(query.data)} reload={() => void query.refetch()} />;
}

function Directory({ users, reload }: { users: UserItem[]; reload: () => void }) {
  const { can, role: myRole } = usePermissions();
  const me = useSession()?.user;
  const mayWrite = can("users.write");
  const canManage = (r: Role): boolean => (myRole ? RANK[myRole] >= RANK[r] : false);

  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<UserItem | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(q));
  }, [users, search]);

  const onCallCount = users.filter((u) => u.onCall).length;

  return (
    <div className="stack">
      {err && <Notice tone="red">{err}</Notice>}

      <Panel
        title={
          <span className="people-title">
            People <span className="muted">· {users.length}</span>
            {onCallCount > 0 && <Badge tone="green">{onCallCount} on call</Badge>}
          </span>
        }
        action={
          mayWrite ? (
            <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
              Add person
            </Button>
          ) : undefined
        }
      >
        <div className="people-search">
          <Icon name="search" size={16} />
          <input value={search} placeholder="Search by name, email, or role" onChange={(e) => setSearch(e.target.value)} />
        </div>

        {shown.length === 0 ? (
          <EmptyState icon="users" title="No one matches" text="Try a different search." />
        ) : (
          <ul className="people-list">
            {shown.map((u) => {
              const st = statusOf(u);
              return (
                <li
                  key={u.id}
                  className="people-row"
                  role={mayWrite && canManage(u.role) ? "button" : undefined}
                  tabIndex={mayWrite && canManage(u.role) ? 0 : undefined}
                  onClick={() => mayWrite && canManage(u.role) && setEditing(u)}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && mayWrite && canManage(u.role)) {
                      e.preventDefault();
                      setEditing(u);
                    }
                  }}
                >
                  <span className="people-avatar" aria-hidden="true">
                    {u.name.slice(0, 1).toUpperCase()}
                  </span>
                  <div className="people-who">
                    <div className="people-name">
                      {u.name}
                      {me?.id === u.id && <span className="people-you">you</span>}
                    </div>
                    <div className="muted people-email">{u.email}</div>
                  </div>
                  <Badge tone={u.role === "owner" ? "green" : "neutral"}>{roleLabel(u.role)}</Badge>
                  <Badge tone={st.tone}>{st.label}</Badge>
                  <span className="people-oncall">
                    {u.onCall ? (
                      <Badge tone="green" dot>
                        On call
                      </Badge>
                    ) : u.oncallEligible ? (
                      <Badge tone="neutral">On-call eligible</Badge>
                    ) : (
                      <span className="muted">—</span>
                    )}
                    {u.phone && <span className="muted nd-mono people-phone">{u.phone}</span>}
                  </span>
                  <span className="muted people-seen">{lastSeen(u.lastLoginAt)}</span>
                  {mayWrite && canManage(u.role) && <Icon name="chevron" size={16} />}
                </li>
              );
            })}
          </ul>
        )}
        {!mayWrite && <Notice>You do not have permission to manage people. This is read-only.</Notice>}
      </Panel>

      {adding && (
        <AddPerson
          canManage={canManage}
          onClose={() => setAdding(false)}
          onDone={() => {
            setAdding(false);
            reload();
          }}
          onError={setErr}
        />
      )}
      {editing && (
        <EditPerson
          user={editing}
          isMe={me?.id === editing.id}
          canManage={canManage}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            reload();
          }}
          onError={setErr}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- add

function AddPerson({
  canManage,
  onClose,
  onDone,
  onError,
}: {
  canManage: (r: Role) => boolean;
  onClose: () => void;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const [setPw, setSetPw] = useState(true);
  const [password, setPassword] = useState("");
  const [oncall, setOncall] = useState(false);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const assignable = ROLES.filter(canManage);
  const ok = name.trim() && /.+@.+\..+/.test(email) && (!setPw || password.length >= 10) && (!oncall || phone.trim());

  const submit = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.inviteUser({
        name: name.trim(),
        email: email.trim(),
        role,
        ...(setPw ? { password } : {}),
        ...(oncall ? { oncallEligible: true } : {}),
        ...(oncall && phone.trim() ? { phone: phone.trim() } : {}),
      });
      onDone();
    } catch (e) {
      onError((e as ApiError | null)?.detail ?? "That account could not be created.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Add a person"
      description="Create an account with a password now, or send an invite for them to set their own."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} disabled={!ok || busy} onClick={() => void submit()}>
            {setPw ? "Create account" : "Send invite"}
          </Button>
        </>
      }
    >
      <Field label="Full name">
        <input value={name} placeholder="Dana Ruiz" onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Email">
        <input type="email" value={email} placeholder="dana@yourcompany.com" onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <Field label="Role" hint="You can only assign roles at or below your own.">
        <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
          {assignable.map((r) => (
            <option key={r} value={r}>
              {roleLabel(r)}
            </option>
          ))}
        </select>
      </Field>

      <Toggle
        label="Set a password now"
        description="Off = send an invite and let them choose their own password."
        checked={setPw}
        onChange={() => setSetPw((v) => !v)}
      />
      {setPw && (
        <Field label="Password" hint="At least 10 characters. Hand it to them securely — it is never shown again.">
          <div className="people-pw">
            <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 10 characters" />
            <Button onClick={() => setPassword(strongPassword())}>Generate</Button>
          </div>
        </Field>
      )}

      <Toggle
        label="On-call eligible"
        description="Can be put on the paging ladder. Add them to it on the On-call page."
        checked={oncall}
        onChange={() => setOncall((v) => !v)}
      />
      {oncall && (
        <Field label="Mobile" hint="Where an on-call page would call. +1 then the number.">
          <input value={phone} placeholder="+13045551234" onChange={(e) => setPhone(e.target.value)} />
        </Field>
      )}
    </Modal>
  );
}

// --------------------------------------------------------------------------- edit

function EditPerson({
  user,
  isMe,
  canManage,
  onClose,
  onDone,
  onError,
}: {
  user: UserItem;
  isMe: boolean;
  canManage: (r: Role) => boolean;
  onClose: () => void;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState<Role>(user.role);
  const [oncall, setOncall] = useState(user.oncallEligible);
  const [phone, setPhone] = useState(user.phone ?? "");
  const [busy, setBusy] = useState<string | null>(null);
  const [resetPw, setResetPw] = useState("");
  const disabled = Boolean(user.disabledAt);

  const assignable = ROLES.filter(canManage);
  const run = async (tag: string, fn: () => Promise<unknown>): Promise<void> => {
    setBusy(tag);
    try {
      await fn();
      onDone();
    } catch (e) {
      onError((e as ApiError | null)?.detail ?? "That change could not be saved.");
    } finally {
      setBusy(null);
    }
  };

  const saveProfile = (): Promise<void> =>
    run("save", () =>
      api.updateUser(user.id, {
        name: name.trim(),
        ...(role !== user.role && !isMe ? { role } : {}),
        oncallEligible: oncall,
        phone: oncall ? (phone.trim() || null) : null,
      }),
    );

  return (
    <Modal
      open
      onClose={onClose}
      title={user.name}
      description={user.email}
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button variant="primary" loading={busy === "save"} onClick={() => void saveProfile()}>
            Save changes
          </Button>
        </>
      }
    >
      <Field label="Full name">
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      <Field label="Role" hint={isMe ? "You cannot change your own role — ask another owner." : "At or below your own."}>
        <select value={role} disabled={isMe} onChange={(e) => setRole(e.target.value as Role)}>
          {(assignable.includes(role) ? assignable : [role, ...assignable]).map((r) => (
            <option key={r} value={r}>
              {roleLabel(r)}
            </option>
          ))}
        </select>
      </Field>

      <Toggle
        label="On-call eligible"
        description="Can be put on the paging ladder. Turning this off takes them off it."
        checked={oncall}
        onChange={() => setOncall((v) => !v)}
      />
      {oncall && (
        <Field label="Mobile" hint="Where an on-call page calls.">
          <input value={phone} placeholder="+13045551234" onChange={(e) => setPhone(e.target.value)} />
        </Field>
      )}

      <div className="people-divider" />

      <Field label="Reset password" hint="Sets a new password and signs them out everywhere.">
        <div className="people-pw">
          <input value={resetPw} placeholder="At least 10 characters" onChange={(e) => setResetPw(e.target.value)} />
          <Button onClick={() => setResetPw(strongPassword())}>Generate</Button>
          <Button
            variant="primary"
            loading={busy === "pw"}
            disabled={resetPw.length < 10}
            onClick={() => void run("pw", () => api.resetPassword(user.id, { password: resetPw }))}
          >
            Reset
          </Button>
        </div>
      </Field>

      <div className="people-divider" />

      {isMe ? (
        <Notice>This is your account — another owner or admin handles disabling or your role.</Notice>
      ) : disabled ? (
        <Button loading={busy === "enable"} onClick={() => void run("enable", () => api.updateUser(user.id, { disabled: false }))}>
          <Icon name="check" /> Re-enable this account
        </Button>
      ) : (
        <Button
          variant="danger"
          loading={busy === "disable"}
          onClick={() => void run("disable", () => api.updateUser(user.id, { disabled: true }))}
        >
          <Icon name="lock" /> Disable account
        </Button>
      )}
    </Modal>
  );
}

export default People;
